import React, {
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect
} from "react";
import {
  View,
  Text,
  StyleSheet,
  Alert,
  Platform,
  KeyboardAvoidingView,
  ScrollView,
  TouchableOpacity
} from "react-native";
import { useTranslation } from "../../../../../i18n/useTranslation";
import Colors from "../../../../../constants/colors";
import { consumerLookup } from "../../../../../services/send.service";
import { SessionState, useSessionStore } from "../../../../../../../state/src/useSessionStore";
import EnglishFlag from "../../../../../../../../assets/images/EnglishFlag.svg";
import RightArrow from "../../../../../../../../assets/images/chevron-right.svg";
import FloatingTextInputComponent from "../../../common/FloatingTextInputComponent";

type Props = {
  onValidationChange?: (valid: boolean) => void;
  testID?: string;
};

export type AccountInformationStepHandle = {
  submit: () => Promise<"ok" | "notfound">;
  accountNumberSubmit: () => Promise<boolean>;
};

/** Bill Pay: biller account number max length (product rule). */
const BILLER_ACCOUNT_NUMBER_MAX_LENGTH = 30;

const AccountInformationStep = forwardRef<AccountInformationStepHandle, Props>(({ onValidationChange, testID }, ref) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState({
    billerAccountNumber: "",
  });
  const selectedBillerInformation = useSessionStore((s: SessionState) => (s as any).selectedBillerInformationStore);
  const agentId = useSessionStore((s: SessionState) => s.agentId);
  const unitProfileId = useSessionStore((s: SessionState) => s.unitProfileId);
  const setIsNewConsumer = useSessionStore((s: SessionState) => (s as any).setIsNewConsumer);
  const [phoneNumber, setPhoneNumber] = useState("");
  const storedBillerAccountNumber = useSessionStore((s: SessionState) => (s as any).billPayBillerAccountNumberStore);
  const setBillPayBillerAccountNumberStore = useSessionStore((s: SessionState) => (s as any).setBillPayBillerAccountNumberStore);
  const [billerAccountNumber, setBillerAccountNumber] = useState(() =>
    String((useSessionStore.getState() as any).billPayBillerAccountNumberStore ?? "").slice(
      0,
      BILLER_ACCOUNT_NUMBER_MAX_LENGTH
    )
  );

  useEffect(() => {
    const s = String(storedBillerAccountNumber ?? "").slice(0, BILLER_ACCOUNT_NUMBER_MAX_LENGTH);
    setBillerAccountNumber(s);
  }, [storedBillerAccountNumber]);
  const trimmedAccount = billerAccountNumber.trim();
  const isValid =
    trimmedAccount.length > 0 && trimmedAccount.length <= BILLER_ACCOUNT_NUMBER_MAX_LENGTH;
        
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [isValid, onValidationChange]);

  const submit = async (): Promise<"ok" | "notfound"> => {
    if (phoneNumber.length !== 10) {
      Alert.alert("Error", t("PHONE_STEP_ERROR_INVALID"));
      throw new Error("Invalid phone number");
    }
    if (!agentId || !unitProfileId) {
      Alert.alert("Error", t("PHONE_STEP_ERROR_MISSING_LOCATION"));
      throw new Error("Missing agent or unit profile");
    }
    try {
      await consumerLookup(phoneNumber, agentId, unitProfileId);
      setIsNewConsumer?.(false);
      return "ok";
    } catch (error: any) {
      const code = error?.code || error?.ErrorDetail?.ErrorCode;
      if (String(code) === "616") {
        setIsNewConsumer?.(true);
        return "notfound";
      }
      setIsNewConsumer?.(false);
      Alert.alert("Error", t("PHONE_STEP_ERROR_FAILED"));
      throw error;
    }
  };

  useImperativeHandle(ref, () => ({
    submit,
    accountNumberSubmit: async () => {
      const trimmed = billerAccountNumber.trim();
      let billerAccountNumberErr = "";
      if (!trimmed) {
        billerAccountNumberErr = "Account number is required";
      } else if (trimmed.length > BILLER_ACCOUNT_NUMBER_MAX_LENGTH) {
        billerAccountNumberErr = t("PERSON_NAME_ERROR_MAX_CHARS", {
          count: BILLER_ACCOUNT_NUMBER_MAX_LENGTH,
        });
      }
      const newErrors = { billerAccountNumber: billerAccountNumberErr };
      setErrors(newErrors);

      const isValidSubmit = !Object.values(newErrors).some(Boolean);
      return isValidSubmit;
    },
  }));

  const Wrapper = Platform.OS === "web" ? View : View;
  return (
    <Wrapper style={styles.container} testID={testID}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
      >
        <ScrollView
          contentContainerStyle={[styles.scrollContainer, { flexGrow: 1 }]}
          keyboardShouldPersistTaps="handled"
          keyboardDismissMode="on-drag"
        >
          <View style={styles.content}>
            <Text style={styles.title}>{t("TELL_US_MORE_ABOUT_TRANSACTION")}</Text>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24}}>
              <View style={{flexDirection: 'row'}}>
                <View style={styles.flagContainer}>
                  <EnglishFlag width={58} height={58}/>
                </View>
                <View style={{ marginLeft: 36}}>
                  <Text style={styles.companyName}>{String(selectedBillerInformation?.BillerName ?? selectedBillerInformation?.billerName ?? 'Select a biller')}</Text>
                  <Text style={styles.deliveryText}>{String(selectedBillerInformation?.BillerNotes ?? selectedBillerInformation?.billerNotes ?? ' ')}</Text>
                </View>
              </View>
              <TouchableOpacity testID="rightArrowButton">
                <RightArrow width={24} height={24}/>
              </TouchableOpacity>
            </View>
            <FloatingTextInputComponent
              testID="billPayAccountNumber"
              label={"Biller Account Number"}
              value={billerAccountNumber}
              isAutoFocus={true}
              keyboardType="numeric"
              maxLength={BILLER_ACCOUNT_NUMBER_MAX_LENGTH}
              onChangeText={(text) => {
                const next = text.slice(0, BILLER_ACCOUNT_NUMBER_MAX_LENGTH);
                setBillerAccountNumber(next);
                try { setBillPayBillerAccountNumberStore?.(next || null); } catch {}
              }}
              errorText={errors.billerAccountNumber}
              onClearError={() => setErrors((prev) => ({ ...prev, billerAccountNumber: "" }))}
            />
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </Wrapper>
    );
  }
);

export default AccountInformationStep;

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#fff",
    flex: 1
  },
  scrollContainer: {
    paddingHorizontal: 24,
    paddingBottom: 40,
    alignItems: "stretch",
  },
  content: {
    marginTop: 8
  },
  title: {
    fontFamily: 'Rubik',
    fontSize: 28,
    fontWeight: "500",
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 36,
  },
  companyName: {
    fontSize: 16,
    fontFamily: 'Rubik',
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 24,
  },
  deliveryText: {
    fontSize: 12,
    fontFamily: 'Rubik',
    fontWeight: "400",
    color: Colors.titleTextColor,
    lineHeight: 16
  },
  flagContainer: {
    height: 48,
    width: 48,
    borderRadius: 50,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
  },
});